import { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import UploadIcon from "../../../icons/UploadIcon";
import classes from "./Images.module.css";

const MAX_FILE_SIZE_MB = 5;

const compressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

const Images = ({ formData, updateFormData }) => {
  const fileInputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const handleFiles = async (files) => {
    const allFiles = Array.from(files);
    const validFiles = [];

    for (const file of allFiles) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB and was skipped.`);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    setCompressing(true);
    try {
      const compressed = await Promise.all(
        validFiles.map((file) => imageCompression(file, compressionOptions))
      );
      updateFormData({ images: [...formData.images, ...compressed] });
    } finally {
      setCompressing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemove = (index) => {
    const updated = [...formData.images];
    updated.splice(index, 1);
    updateFormData({ images: updated });
  };

  const handleDescriptionChange = (e) => {
    updateFormData({ description: e.target.value });
  };

  return (
    <div className={`${classes.wrapper} container`}>
      <h4 className="mb-4">Images & description</h4>

      <h5 className={`card-title ${classes.sectionTitle}`}>
        Car images<span>*</span>
      </h5>

      <div
        className={`${classes.uploadArea} p-4 mt-2 mb-4 text-center ${
          dragOver ? classes.dragOver : ""
        } ${compressing ? classes.compressing : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !compressing && fileInputRef.current.click()}
      >
        <UploadIcon />
        {compressing ? (
          <p className="mb-3 mt-2" style={{ color: "var(--primary-color)" }}>
            Compressing images...
          </p>
        ) : (
          <p className="mb-3 mt-2">
            Drag and drop images here or <br />
            <span className={classes.browseLink}>Browse files</span>
          </p>
        )}
        <p className={`text-muted ${classes.supporText}`}>
          Support: JPG, PNG, WEBP — compressed automatically before upload
        </p>
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          onChange={(e) => handleFiles(e.target.files)}
          className="d-none"
        />
      </div>

      <div className="row mb-4">
        {formData.images.map((file, index) => (
          <div className="col-md-3 mb-3 position-relative" key={index}>
            <div
              className={classes.imagePreview}
              style={{
                backgroundImage: `url(${
                  typeof file === "string" ? file : URL.createObjectURL(file)
                })`,
              }}
            ></div>
            <button
              type="button"
              className={classes.removeBtn}
              onClick={() => handleRemove(index)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <h5 className={`card-title ${classes.sectionTitle}`}>Car description</h5>
      <textarea
        className={`form-control ${classes.descriptionInput} mt-2`}
        rows="5"
        placeholder="Provide a detailed description of the car"
        value={formData.description || ""}
        onChange={handleDescriptionChange}
      ></textarea>
    </div>
  );
};

export default Images;
